import { Form } from 'antd';
import React, {
  FC, useCallback, useEffect, useState,
} from 'react';
import ClientDetailsForm from './ClientDetailsForm';
import { DOCTORS_ROUTE, MEDICARE_AND_HEALTH } from '../../constants/routes';
import FooterNavigator from '../../components/FooterNavigator';
import { StepError, StepLead, StepPage } from '../../components/StepPanel';
import { useAppDispatch, useAppSelector } from '../../hooks/redux-hooks';
import { addConsentsData, selectBookingData, updateBookingClientData } from '../../redux/slices/booking';
import { CONSENTS_MAPPING } from '../../constants/consents';
import Consents from './Consents';
import { scrollToFirstError } from '../../utils/form';
import { removeEmptyStrFields } from '../../utils/object-check';
import { DATE_WITH_DASH } from '../../constants/time-formats';
import analytics from '../../infra/analytics';
import { NEW_PATIENT_REGISTRATION, PAGE_VIEW_EVENT, SITE_NAME } from '../../constants/analytics';

const ClientDetails: FC = () => {
  const { consents } = useAppSelector(selectBookingData);
  const [isAgreedThePrivacyPolicy, setIsAgreedThePrivacyPolicy] = useState(Boolean(consents?.isAgreedThePrivacyPolicy?.agreed));
  const [consentsHasError, setConsentsHasError] = useState(false);
  const dispatch = useAppDispatch();
  const [form] = Form.useForm();

  useEffect(() => {
    analytics.logPageViewEvent(PAGE_VIEW_EVENT, {
      site: SITE_NAME,
      page_name: NEW_PATIENT_REGISTRATION,
    });
  }, []);

  const handlePrivacyChange = (value: boolean) => {
    setIsAgreedThePrivacyPolicy(value);
    if (value) setConsentsHasError(false);
  };

  const submit = useCallback(async () => {
    setConsentsHasError(!isAgreedThePrivacyPolicy);

    try {
      const values = await form.validateFields();
      if (!isAgreedThePrivacyPolicy) {
        document.getElementById('privacy-consent')?.scrollIntoView({ block: 'center', behavior: 'smooth' });
        return false;
      }
      const {
        nextOfKin, addressResidential, diffPostalAddress, ...otherValues
      } = values;
      const addressPostal = (diffPostalAddress) ? values.addressPostal : addressResidential;

      dispatch(updateBookingClientData({
        ...removeEmptyStrFields(otherValues),
        addressPostal: removeEmptyStrFields(addressPostal),
        addressResidential: removeEmptyStrFields(addressResidential),
        nextOfKin: removeEmptyStrFields(nextOfKin),
        dateOfBirth: values.dateOfBirth.format(DATE_WITH_DASH),
      }));

      dispatch(addConsentsData({
        isAgreedThePrivacyPolicy: { ...CONSENTS_MAPPING.isAgreedThePrivacyPolicy, agreed: true },
      }));

      return true;
    } catch (errorInfo) {
      scrollToFirstError(form, errorInfo);
      return false;
    }
  }, [form, dispatch, isAgreedThePrivacyPolicy]);

  return (
    <StepPage>
      <h2>Your details</h2>
      <StepLead>We use these to create your record and send your confirmation, payment link and video link.</StepLead>

      <ClientDetailsForm form={form} />

      <Consents
        isAgreedThePrivacyPolicy={isAgreedThePrivacyPolicy}
        setIsAgreedThePrivacyPolicy={handlePrivacyChange}
      />

      {consentsHasError && <StepError role="alert">Please tick the privacy consent to continue.</StepError>}

      <FooterNavigator submit={submit} next={MEDICARE_AND_HEALTH} back={DOCTORS_ROUTE} />
    </StepPage>
  );
};

export default ClientDetails;
